import { Autocomplete, TextField, Typography } from '@mui/material';
import { Supplier } from '@core/models/supplier.model';
import { Warehouse } from '@core/models/warehouse.model';
import { Product } from '@core/models/product.model';
import React from 'react';
import InboundProductLinesEditor from './InboundProductLinesEditor';
import InboundSerialNumbersEditor from './InboundSerialNumbersEditor';
import EpcisProductIdentityPreviewPanel from './EpcisProductIdentityPreviewPanel';
import PackageDataMatrixScanField from 'components/batch/PackageDataMatrixScanField';
import { ISgtinScanValidation } from '@core/models/sgtin-scan.model';
import {
    buildStagingLinesFromPreview,
    findIlmdScanMismatches,
    findIlmdStagingMismatches,
    palletModeBannerText,
    parseEpcisPreview,
    productLinesFromJson,
    productLinesToJson,
    toDateInputValue,
    InboundProductLineStaging,
} from './epcis-preview.helpers';
import { serialsFromJson, serialsToJson } from './serial-staging.helpers';

export interface InboundStagingFormState {
    prefferedSupplierId: number | null;
    warehouseId: number | null;
    purchaseOrderNumber: string;
    comment: string;
    productLines: InboundProductLineStaging[];
    /** Legacy single-product fields (when no productLines from EPCIS) */
    productId: number | null;
    lotNumber: string;
    expiryDate: string;
    quantity: string;
    actualPrice: string;
    palletSscc: string;
    palletWeightKg: string;
    serialNumbers: string[];
}

export const emptyInboundStaging = (): InboundStagingFormState => ({
    prefferedSupplierId: null,
    warehouseId: null,
    purchaseOrderNumber: '',
    comment: '',
    productLines: [],
    productId: null,
    lotNumber: '',
    expiryDate: '',
    quantity: '1',
    actualPrice: '',
    palletSscc: '',
    palletWeightKg: '',
    serialNumbers: [],
});

export function stagingFromApi(
    dto?: Record<string, unknown> | null,
    previewJson?: string | null,
    products: Product[] = []
): InboundStagingFormState {
    if (!dto) return emptyInboundStaging();
    const fromJson = productLinesFromJson(dto.productLinesJson as string | undefined);
    const productLines =
        fromJson.length > 0
            ? fromJson
            : buildStagingLinesFromPreview(previewJson, products);
    return {
        prefferedSupplierId: (dto.prefferedSupplierId as number) ?? null,
        warehouseId: (dto.warehouseId as number) ?? null,
        purchaseOrderNumber: (dto.purchaseOrderNumber as string) ?? '',
        comment: (dto.comment as string) ?? '',
        productLines,
        productId: (dto.productId as number) ?? null,
        lotNumber: (dto.lotNumber as string) ?? '',
        expiryDate: toDateInputValue(dto.expiryDate as string),
        quantity: dto.quantity != null ? String(dto.quantity) : '1',
        actualPrice: dto.actualPrice != null ? String(dto.actualPrice) : '',
        palletSscc: (dto.palletSscc as string) ?? '',
        palletWeightKg: dto.palletWeightKg != null ? String(dto.palletWeightKg) : '',
        serialNumbers: serialsFromJson(dto.serializedItemsJson as string | undefined),
    };
}

export function applyPreviewHints(
    staging: InboundStagingFormState,
    previewJson?: string | null,
    products: Product[] = []
): InboundStagingFormState {
    if (!previewJson?.trim()) return staging;
    const summary = parseEpcisPreview(previewJson);
    if (!summary) return staging;
    let next: InboundStagingFormState = { ...staging };
    if (summary.purchaseOrderHint && !staging.purchaseOrderNumber.trim()) {
        next = { ...next, purchaseOrderNumber: summary.purchaseOrderHint };
    }
    if (summary.productLines.length > 0 && staging.productLines.length === 0) {
        next = {
            ...next,
            productLines: buildStagingLinesFromPreview(previewJson, products),
        };
    }
    if (summary.productLines.length === 1 && next.productLines.length === 0) {
        const lines = buildStagingLinesFromPreview(previewJson, products);
        const line = lines[0];
        if (line) {
            next = {
                ...next,
                productId: line.productId,
                lotNumber: line.lotNumber,
                expiryDate: line.expiryDate,
                quantity: line.quantity,
                palletSscc: line.palletSscc,
                serialNumbers: line.serialNumbers,
            };
        }
    }
    return next;
}

export function toStagingSavePayload(staging: InboundStagingFormState): Record<string, unknown> {
    const useMulti = staging.productLines.length > 0;
    if (useMulti) {
        const first = staging.productLines[0];
        return {
            prefferedSupplierId: staging.prefferedSupplierId ?? undefined,
            warehouseId: staging.warehouseId ?? undefined,
            purchaseOrderNumber: staging.purchaseOrderNumber,
            comment: staging.comment,
            productLinesJson: productLinesToJson(staging.productLines),
            productId: first?.productId ?? undefined,
            lotNumber: first?.lotNumber,
            expiryDate: first?.expiryDate || undefined,
            quantity: first ? Number(first.quantity) || first.serialNumbers.length : 1,
            actualPrice:
                first?.actualPrice.trim() !== '' ? Number(first.actualPrice) : undefined,
            palletSscc: first?.palletSscc.trim() || undefined,
            palletWeightKg:
                first?.palletWeightKg.trim() !== '' ? Number(first.palletWeightKg) : undefined,
            serializedItemsJson: first ? serialsToJson(first.serialNumbers) : undefined,
        };
    }
    const actualPriceNum = staging.actualPrice.trim() !== '' ? Number(staging.actualPrice) : undefined;
    const palletWeightNum = staging.palletWeightKg.trim() !== '' ? Number(staging.palletWeightKg) : undefined;
    return {
        prefferedSupplierId: staging.prefferedSupplierId ?? undefined,
        productId: staging.productId ?? undefined,
        warehouseId: staging.warehouseId ?? undefined,
        lotNumber: staging.lotNumber,
        expiryDate: staging.expiryDate || undefined,
        quantity: Number(staging.quantity) || 1,
        actualPrice: actualPriceNum != null && !Number.isNaN(actualPriceNum) ? actualPriceNum : undefined,
        palletSscc: staging.palletSscc.trim() || undefined,
        palletWeightKg: palletWeightNum != null && !Number.isNaN(palletWeightNum) ? palletWeightNum : undefined,
        purchaseOrderNumber: staging.purchaseOrderNumber,
        comment: staging.comment,
        serializedItemsJson: serialsToJson(staging.serialNumbers),
    };
}

type Props = {
    staging: InboundStagingFormState;
    setStaging: React.Dispatch<React.SetStateAction<InboundStagingFormState>>;
    disabled?: boolean;
    suppliers: Supplier[];
    products: Product[];
    warehouses: Warehouse[];
    validationPreviewJson?: string | null;
};

const InboundAdminStagingForm: React.FC<Props> = ({
    staging,
    setStaging,
    disabled = false,
    suppliers,
    products,
    warehouses,
    validationPreviewJson,
}) => {
    const [singleLineScanMismatch, setSingleLineScanMismatch] = React.useState<string | null>(null);
    const previewSummary = parseEpcisPreview(validationPreviewJson);
    const supplierPreviewLine = previewSummary?.productLines?.[0];
    const singleLineStagingIlmdWarnings = React.useMemo(
        () =>
            findIlmdStagingMismatches(
                supplierPreviewLine?.lotNumber,
                supplierPreviewLine?.expiryDate,
                staging.lotNumber,
                staging.expiryDate
            ),
        [
            supplierPreviewLine?.lotNumber,
            supplierPreviewLine?.expiryDate,
            staging.lotNumber,
            staging.expiryDate,
        ]
    );
    const multiLine = staging.productLines.length > 1 || previewSummary?.multiProduct;
    const poMismatch =
        previewSummary?.purchaseOrderHint &&
        staging.purchaseOrderNumber.trim() &&
        staging.purchaseOrderNumber.trim() !== previewSummary.purchaseOrderHint;
    const selectedSupplier = suppliers.find((s) => s.id === staging.prefferedSupplierId) ?? null;
    const selectedWarehouse = warehouses.find((w) => w.id === staging.warehouseId) ?? null;
    const selectedProduct = products.find((p) => p.id === staging.productId) ?? null;

    return (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {previewSummary && (previewSummary.multiProduct || previewSummary.productLineCount > 1) ? (
                <div className="md:col-span-2 rounded-md border border-emerald-300 bg-emerald-50 p-3">
                    <Typography variant="body2" className="font-medium text-emerald-900">
                        {palletModeBannerText(previewSummary)}
                    </Typography>
                </div>
            ) : null}

            <Autocomplete
                disabled={disabled}
                options={suppliers}
                getOptionLabel={(option: Supplier) =>
                    option.businessName || option.email || `Supplier #${option.id}`
                }
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={selectedSupplier}
                onChange={(_, newValue) =>
                    setStaging((s) => ({ ...s, prefferedSupplierId: newValue?.id ?? null }))
                }
                renderInput={(params) => <TextField {...params} label="Preferred supplier *" size="small" />}
                sx={{ width: '100%' }}
            />
            <Autocomplete
                disabled={disabled}
                options={warehouses}
                getOptionLabel={(option: Warehouse) => option.name || `Warehouse #${option.id}`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={selectedWarehouse}
                onChange={(_, newValue) => setStaging((s) => ({ ...s, warehouseId: newValue?.id ?? null }))}
                renderInput={(params) => <TextField {...params} label="Warehouse *" size="small" />}
                sx={{ width: '100%' }}
            />
            <TextField
                disabled={disabled}
                fullWidth
                size="small"
                label={multiLine ? 'Purchase order number *' : 'Purchase order number'}
                value={staging.purchaseOrderNumber}
                onChange={(e) => setStaging((s) => ({ ...s, purchaseOrderNumber: e.target.value }))}
                className="md:col-span-2"
                helperText={
                    previewSummary?.purchaseOrderHint
                        ? `EPCIS PO: ${previewSummary.purchaseOrderHint}`
                        : undefined
                }
                error={Boolean(poMismatch)}
            />
            {previewSummary &&
            (previewSummary.purchaseOrderHint ||
                previewSummary.invoiceHint ||
                previewSummary.despatchAdviceHint) ? (
                <div className="md:col-span-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
                    <span className="font-medium">EPCIS shipping: </span>
                    {previewSummary.purchaseOrderHint ? (
                        <span className="mr-3">PO {previewSummary.purchaseOrderHint}</span>
                    ) : null}
                    {previewSummary.invoiceHint ? (
                        <span className="mr-3">Invoice {previewSummary.invoiceHint}</span>
                    ) : null}
                    {previewSummary.despatchAdviceHint ? (
                        <span>Shipment {previewSummary.despatchAdviceHint}</span>
                    ) : null}
                </div>
            ) : null}
            {validationPreviewJson?.trim() && previewSummary && !previewSummary.despatchAdviceHint ? (
                <div className="md:col-span-2 rounded-md border border-amber-300 bg-amber-50 p-2">
                    <Typography variant="body2" className="text-amber-900">
                        Shipment ID not found in preview — re-run validation before approve.
                    </Typography>
                </div>
            ) : null}
            {validationPreviewJson?.trim() && previewSummary && !previewSummary.invoiceHint ? (
                <div className="md:col-span-2 rounded-md border border-amber-300 bg-amber-50 p-2">
                    <Typography variant="body2" className="text-amber-900">
                        Invoice not found in preview — batch invoice may be empty unless you re-run validation.
                    </Typography>
                </div>
            ) : null}
            {poMismatch ? (
                <div className="md:col-span-2 rounded-md border border-amber-300 bg-amber-50 p-2">
                    <Typography variant="body2" className="text-amber-900">
                        Purchase order does not match EPCIS file ({previewSummary?.purchaseOrderHint}).
                    </Typography>
                </div>
            ) : null}
            <TextField
                disabled={disabled}
                fullWidth
                size="small"
                label="Comment"
                multiline
                minRows={2}
                className="md:col-span-2"
                value={staging.comment}
                onChange={(e) => setStaging((s) => ({ ...s, comment: e.target.value }))}
            />

            {staging.productLines.length > 0 ? (
                <InboundProductLinesEditor
                    lines={staging.productLines}
                    onChange={(productLines) => setStaging((s) => ({ ...s, productLines }))}
                    disabled={disabled}
                    products={products}
                    previewSummary={previewSummary}
                    previewJson={validationPreviewJson}
                />
            ) : (
                <>
                    <Autocomplete
                        disabled={disabled}
                        options={products}
                        getOptionLabel={(option: Product) => {
                            const code = option.ndcCode || option.gtin || option.barcode || '';
                            return option.itemName
                                ? code
                                    ? `${option.itemName} (${code})`
                                    : option.itemName
                                : `Product #${option.id}`;
                        }}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        value={selectedProduct}
                        onChange={(_, newValue) =>
                            setStaging((s) => ({ ...s, productId: newValue?.id ?? null }))
                        }
                        renderInput={(params) => <TextField {...params} label="Product" size="small" />}
                        sx={{ width: '100%' }}
                    />
                    <TextField
                        disabled={disabled}
                        fullWidth
                        size="small"
                        label="Product lot (manufacturer)"
                        value={staging.lotNumber}
                        onChange={(e) => setStaging((s) => ({ ...s, lotNumber: e.target.value }))}
                    />
                    <TextField
                        disabled={disabled}
                        fullWidth
                        size="small"
                        label="Expiry date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={staging.expiryDate}
                        onChange={(e) => setStaging((s) => ({ ...s, expiryDate: e.target.value }))}
                    />
                    {singleLineStagingIlmdWarnings.length > 0 ? (
                        <div className="md:col-span-2 rounded-md border border-amber-300 bg-amber-50 p-2">
                            <Typography variant="body2" className="text-amber-900">
                                Supplier ILMD mismatch: {singleLineStagingIlmdWarnings.join('; ')}
                            </Typography>
                        </div>
                    ) : null}
                    <TextField
                        disabled={disabled}
                        fullWidth
                        size="small"
                        label="Quantity"
                        type="number"
                        inputProps={{ min: 1 }}
                        value={staging.quantity}
                        onChange={(e) => setStaging((s) => ({ ...s, quantity: e.target.value }))}
                    />
                    <TextField
                        disabled={disabled}
                        fullWidth
                        size="small"
                        label="Actual price"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        value={staging.actualPrice}
                        onChange={(e) => setStaging((s) => ({ ...s, actualPrice: e.target.value }))}
                    />
                    <TextField
                        disabled={disabled}
                        fullWidth
                        size="small"
                        label="Pallet SSCC"
                        value={staging.palletSscc}
                        onChange={(e) => setStaging((s) => ({ ...s, palletSscc: e.target.value }))}
                    />
                    <TextField
                        disabled={disabled}
                        fullWidth
                        size="small"
                        label="Pallet weight (kg)"
                        type="number"
                        inputProps={{ min: 0, step: 0.1 }}
                        value={staging.palletWeightKg}
                        onChange={(e) => setStaging((s) => ({ ...s, palletWeightKg: e.target.value }))}
                    />
                    <div className="md:col-span-2">
                        <EpcisProductIdentityPreviewPanel
                            productId={staging.productId}
                            sgtinItemKey={
                                previewSummary?.productLines?.[0]?.sgtinItemKey ??
                                previewSummary?.sgtinItemKey
                            }
                        />
                    </div>
                    <div className="md:col-span-2">
                        <PackageDataMatrixScanField
                            disabled={disabled}
                            productId={staging.productId}
                            onValidated={(result: ISgtinScanValidation) => {
                                if (!result.valid || !result.serialNumber?.trim()) return;
                                const serial = result.serialNumber.trim();
                                setStaging((s) => ({
                                    ...s,
                                    serialNumbers: s.serialNumbers.includes(serial)
                                        ? s.serialNumbers
                                        : [...s.serialNumbers, serial],
                                }));
                                const scanWarnings = findIlmdScanMismatches(
                                    supplierPreviewLine?.lotNumber,
                                    supplierPreviewLine?.expiryDate,
                                    result.lotNumber,
                                    result.expiryDate
                                );
                                setSingleLineScanMismatch(
                                    scanWarnings.length ? scanWarnings.join('; ') : null
                                );
                            }}
                        />
                    </div>
                    {singleLineScanMismatch ? (
                        <div className="md:col-span-2 rounded-md border border-amber-300 bg-amber-50 p-2">
                            <Typography variant="body2" className="text-amber-900">
                                DataMatrix ILMD mismatch: {singleLineScanMismatch}
                            </Typography>
                        </div>
                    ) : null}
                    <InboundSerialNumbersEditor
                        serials={staging.serialNumbers}
                        onChange={(serialNumbers) => setStaging((s) => ({ ...s, serialNumbers }))}
                        disabled={disabled}
                        previewJson={validationPreviewJson}
                    />
                </>
            )}
        </div>
    );
};

export default InboundAdminStagingForm;
